import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Boxes, Loader2, Plus, Trash2, Save, GripVertical } from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

export default function CondizioniVendita() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [articoli, setArticoli] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/settings/condizioni-vendita")
      .then((r) => setArticoli((r.data.articoli || []).map((a) => ({ titolo: a.titolo || "", testo: (a.paragrafi || []).join("\n\n") }))))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const setRow = (i, k, v) => setArticoli((arr) => arr.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const remove = (i) => setArticoli((arr) => arr.filter((_, idx) => idx !== i));
  const move = (i, dir) => setArticoli((arr) => {
    const j = i + dir;
    if (j < 0 || j >= arr.length) return arr;
    const next = [...arr];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const add = () => setArticoli((arr) => [...arr, { titolo: "", testo: "" }]);

  const save = async () => {
    const payload = articoli
      .map((a) => ({
        titolo: (a.titolo || "").trim(),
        paragrafi: (a.testo || "").split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean),
      }))
      .filter((a) => a.titolo);
    if (payload.some((a) => !a.paragrafi.length)) {
      toast.error("Ogni articolo deve avere almeno un paragrafo di testo");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings/condizioni-vendita", { articoli: payload });
      setArticoli((data.articoli || []).map((a) => ({ titolo: a.titolo || "", testo: (a.paragrafi || []).join("\n\n") })));
      toast.success("Condizioni di vendita salvate");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6" data-testid="condizioni-vendita-page">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary/10 p-2"><Boxes className="h-5 w-5 text-primary" strokeWidth={1.5} /></div>
          <div>
            <h1 className="text-2xl font-bold leading-tight">Condizioni vendita materiale</h1>
            <p className="text-sm text-muted-foreground">Condizioni Generali di Vendita e Consegna a Consumatori · richiamate nel PDF del Preventivo</p>
          </div>
        </div>
        {isAdmin && (
          <Button onClick={save} disabled={saving || loading} data-testid="save-condizioni-vendita-btn" className="font-semibold">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Salva condizioni
          </Button>
        )}
      </div>

      {!isAdmin && (
        <p className="text-xs text-muted-foreground mb-4">Solo gli amministratori possono modificare questi testi.</p>
      )}

      {loading ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground mb-1">Ogni articolo ha un <b>titolo</b> e un <b>testo</b>. Separa i paragrafi con una riga vuota.</p>
          {articoli.map((c, i) => (
            <Card key={i} className="rounded-lg border-border p-4" data-testid={`cv-row-${i}`}>
              <div className="flex items-start gap-3">
                {isAdmin && (
                  <div className="flex flex-col gap-1 pt-1 text-muted-foreground">
                    <button onClick={() => move(i, -1)} disabled={i === 0} className="disabled:opacity-30 hover:text-foreground" data-testid={`cv-up-${i}`}>▲</button>
                    <GripVertical className="h-4 w-4 opacity-40" />
                    <button onClick={() => move(i, 1)} disabled={i === articoli.length - 1} className="disabled:opacity-30 hover:text-foreground" data-testid={`cv-down-${i}`}>▼</button>
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Titolo</Label>
                    <Input value={c.titolo} readOnly={!isAdmin} onChange={(e) => setRow(i, "titolo", e.target.value)} placeholder="1. PREMESSE E AMBITO DI APPLICAZIONE" data-testid={`cv-titolo-${i}`} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Testo (paragrafi separati da riga vuota)</Label>
                    <Textarea value={c.testo} readOnly={!isAdmin} onChange={(e) => setRow(i, "testo", e.target.value)} rows={6} data-testid={`cv-testo-${i}`} />
                  </div>
                </div>
                {isAdmin && (
                  <Button variant="ghost" size="sm" onClick={() => remove(i)} className="text-destructive hover:text-destructive shrink-0" data-testid={`cv-remove-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </Card>
          ))}
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={add} data-testid="cv-add">
              <Plus className="h-4 w-4 mr-1.5" /> Aggiungi articolo
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
